import { PROVIDERS, type AgentAccount, type Chat } from "@codvero/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { Combine, Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { accountsApi, modelsApi } from "@/api/accounts.ts";
import { chatsApi } from "@/api/chats.ts";
import { ActivityChip, type AgentActivity } from "@/features/chat/TeamRoster.tsx";
import { captureBus } from "@/features/projects/ProjectPage.tsx";
import { cn } from "@/lib/cn.ts";
import { Alert, Button, Select } from "@/ui/primitives.tsx";
import { ProviderIcon } from "@/ui/ProviderIcon.tsx";
import { useApiError } from "@/ui/useApiError.ts";

/** A multi chat and its members, polled quickly while any provider is working. */
export function useMultiChat(chatId: string | null | undefined) {
  return useQuery({
    queryKey: ["multi", chatId],
    queryFn: () => chatsApi.multi(chatId!),
    enabled: !!chatId,
    refetchInterval: (q) => (q.state.data?.members.some((m) => m.status === "running") || q.state.data?.combine?.status === "running" ? 2500 : 10_000),
  });
}

export const accountOf = (chat: Chat, accounts: AgentAccount[]) => accounts.find((a) => a.id === chat.agentAccountId) ?? null;
export const providerName = (chat: Chat, accounts: AgentAccount[]) => {
  const a = accountOf(chat, accounts);
  return a ? PROVIDERS[a.provider].label : chat.title;
};
/** The @handle that addresses a member in the composer: its provider id (claude, codex…), numbered if repeated. */
export function memberHandles(members: Chat[], accounts: AgentAccount[]): Map<string, string> {
  const out = new Map<string, string>();
  const seen = new Map<string, number>();
  for (const m of members) {
    const p = accountOf(m, accounts)?.provider ?? "provider";
    const n = (seen.get(p) ?? 0) + 1;
    seen.set(p, n);
    out.set(m.id, n === 1 ? p : `${p}${n}`);
  }
  return out;
}
export const activityOf = (c: Chat): AgentActivity => (c.status === "running" ? "working" : "ready");

/**
 * The Chat section's "Providers" tool, the counterpart of the project team panel: who is in this conversation and
 * what each is doing; tap to address one in the composer, tick to pin it (every message goes to it), add another
 * provider, or combine the latest answers.
 */
export function ProvidersPanel() {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const { chatId } = useParams({ strict: false }) as { chatId?: string };
  const chat = useQuery({ queryKey: ["chat-kind", chatId], queryFn: () => chatsApi.get(chatId!), enabled: !!chatId, staleTime: Infinity });
  const isMulti = chat.data?.kind === "multi";
  const multi = useMultiChat(isMulti ? chatId : null);
  const accounts = useQuery({ queryKey: ["accounts"], queryFn: accountsApi.list, staleTime: 60_000 });
  const all = accounts.data ?? [];
  const connected = all.filter((a) => a.status === "connected");
  const members = multi.data?.members ?? [];
  const handles = memberHandles(members, all);
  const pinned = multi.data?.chat.agentAccountId ?? null;
  const refresh = () => void qc.invalidateQueries({ queryKey: ["multi", chatId] });
  const pin = useMutation({ mutationFn: (accountId: string | null) => chatsApi.update(chatId!, { agentAccountId: accountId }), onSuccess: refresh });
  const add = useMutation({ mutationFn: (accountId: string) => chatsApi.addMember(chatId!, accountId), onSuccess: refresh });
  const [combineWith, setCombineWith] = useState<string | null>(null);
  const combineAccount = connected.find((a) => a.id === combineWith) ?? connected.find((a) => a.provider === "claude") ?? connected[0];
  const combine = useMutation({ mutationFn: () => chatsApi.combine(chatId!, combineAccount!.id), onSuccess: refresh });
  const err = pin.error ?? add.error ?? combine.error;
  const working = members.some((m) => m.status === "running");
  const outside = connected.filter((a) => !members.some((m) => m.agentAccountId === a.id));

  if (!isMulti) {
    // the new-chat screen (or an older single-provider chat): which providers are ready to be picked
    return (
      <div className="space-y-2 p-3">
        <p className="text-xs text-fg-muted">{t("providers.pickHint")}</p>
        {connected.map((a) => (
          <div key={a.id} className="flex items-center gap-3 rounded-xl border border-border bg-surface px-3 py-2.5">
            <ProviderIcon provider={a.provider} />
            <span className="min-w-0 flex-1"><b className="block truncate text-sm font-medium">{PROVIDERS[a.provider].label}</b><small className="block truncate text-xs text-fg-muted">{a.label}</small></span>
          </div>
        ))}
        {connected.length === 0 && <Alert tone="warn">{t("space.noProviders")}</Alert>}
      </div>
    );
  }

  return (
    <div className="space-y-3 p-3">
      <p className="text-xs text-fg-muted">{working ? t("providers.hintBusy") : t("providers.hint")}</p>
      {err && <Alert tone="danger">{toMsg(err)}</Alert>}
      <div className="space-y-2">
        {members.map((m) => {
          const a = accountOf(m, all);
          const on = !!pinned && pinned === m.agentAccountId;
          return (
            <div key={m.id} className={cn("flex w-full items-center gap-3 rounded-xl border bg-surface px-3 py-2.5", on ? "border-accent/60 bg-accent-soft/20" : "border-border")} style={{ borderInlineStartWidth: 3, borderInlineStartColor: a ? PROVIDERS[a.provider].color : undefined }}>
              <label className="grid size-8 shrink-0 cursor-pointer place-items-center" title={on ? t("team.unpin") : t("team.pin")}>
                <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={on} disabled={pin.isPending} aria-label={on ? t("team.unpin") : t("team.pin")} onChange={() => pin.mutate(on ? null : m.agentAccountId)} />
              </label>
              <button type="button" onClick={() => captureBus.dispatchEvent(new CustomEvent("mention", { detail: handles.get(m.id) }))} className="flex min-w-0 flex-1 items-center gap-3 text-start">
                {a && <ProviderIcon provider={a.provider} />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{providerName(m, all)}</span>
                  <span dir="ltr" className="block truncate font-mono text-xs text-fg-muted">@{handles.get(m.id)}</span>
                </span>
              </button>
              <MemberModel chat={m} account={a} onChanged={refresh} />
              <ActivityChip state={activityOf(m)} />
            </div>
          );
        })}
      </div>

      {outside.length > 0 && (
        <div className="space-y-1.5 border-t border-border pt-3">
          <div className="text-xs font-medium text-fg-muted">{t("providers.add")}</div>
          {outside.map((a) => (
            <button key={a.id} type="button" disabled={add.isPending} onClick={() => add.mutate(a.id)} className="flex w-full items-center gap-3 rounded-xl border border-dashed border-border px-3 py-2 text-start text-sm hover:border-accent/60 disabled:opacity-50">
              <ProviderIcon provider={a.provider} size="xs" />
              <span className="flex-1">{PROVIDERS[a.provider].label}</span>
              <Plus className="size-4 text-fg-muted" />
            </button>
          ))}
          <p className="text-[11px] text-fg-muted">{t("providers.addHint")}</p>
        </div>
      )}

      {members.length > 1 && (
        <div className="space-y-2 border-t border-border pt-3">
          <div className="flex items-center gap-2">
            <Select aria-label={t("multi.combineWith")} value={combineAccount?.id ?? ""} onChange={(e) => setCombineWith(e.target.value)} className="h-9 flex-1 text-xs">
              {connected.map((a) => <option key={a.id} value={a.id}>{t("multi.combineWith")}: {PROVIDERS[a.provider].label}</option>)}
            </Select>
            <Button size="sm" variant="secondary" loading={combine.isPending} disabled={working || !combineAccount || !members.some((m) => m.lastSeq > 0)} onClick={() => combine.mutate()}><Combine className="size-4" /> {t("multi.combine")}</Button>
          </div>
          <p className="text-[11px] text-fg-muted">{t("multi.combineHint")}</p>
        </div>
      )}
    </div>
  );
}

export function MemberModel({ chat, account, onChanged, className }: { chat: Chat; account: AgentAccount | null; onChanged: () => void; className?: string }) {
  const { t } = useTranslation();
  const provider = account?.provider ?? "claude";
  const catalog = useQuery({ queryKey: ["models", provider, account?.id ?? null], queryFn: () => modelsApi.catalog(provider, account?.id ?? null), enabled: !!account, staleTime: 5 * 60_000 });
  const models = catalog.data?.models ?? PROVIDERS[provider].models.map((m) => ({ id: m.id, label: m.label }));
  const list = models.some((m) => m.id === chat.model) ? models : [...models, { id: chat.model, label: chat.model }];
  const set = useMutation({ mutationFn: (model: string) => chatsApi.update(chat.id, { model }), onSuccess: onChanged });
  return (
    <Select aria-label={t("chat.chooseModel")} value={chat.model} disabled={chat.status === "running" || set.isPending} onChange={(e) => set.mutate(e.target.value)} className={cn("h-8 w-auto max-w-[7.5rem] shrink px-1.5 text-[11px]", className)}>
      {list.map((m) => <option key={m.id} value={m.id}>{m.id === "default" ? t("chat.models.default") : m.label}</option>)}
    </Select>
  );
}

